import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { action, ClassModel, register, types } from "@gadgetinc/mobx-quick-tree";
import { compact } from "lodash";
import { DateTime } from "luxon";
import { ServerContract, ServerContractStatusCode } from "./ServerContract";
import { contentHash } from "./utils";

@register
export class BankConfirmationDetails extends ClassModel({
  paymentIntentId: types.string,
  requiresCustomerApproval: types.boolean,
  completesAt: types.number,
  acknowledged: types.boolean,
}) {
  get contentHash() {
    return contentHash([this.paymentIntentId, this.requiresCustomerApproval, this.completesAt, this.acknowledged]);
  }

  @action
  acknowledge() {
    this.acknowledged = true;
  }
}

export const PurchaseAIProposalCreditWorkflowIdPrefix = "ProcessProposalCreditPurchase";

@register
export class PendingInvoiceDetails extends ClassModel({
  invoiceId: types.string,
  failedPaymentIntentId: types.maybeNull(types.string),
  failedPaymentMessage: types.maybeNull(types.string),
}) {}

export const pendingPaymentTypeForWorkflowId = (workflowId: string) => {
  if (workflowId.startsWith(PurchaseAIProposalCreditWorkflowIdPrefix)) {
    return "credit-purchase";
  }
  return "plan-change";
};

@register
export class BillingContract extends ServerContract.extend({
  workflowId: types.maybeNull(types.string),
  bankConfirmationDetails: types.maybeNull(BankConfirmationDetails),
  pendingInvoices: types.optional(types.array(PendingInvoiceDetails), []),
}) {
  get contentHash() {
    return contentHash(compact([this.workflowId, this.status.code, this.bankConfirmationDetails?.contentHash]));
  }

  @action
  setPendingInvoices(pendingInvoices: PendingInvoiceDetails[]) {
    this.pendingInvoices.replace(pendingInvoices);
  }

  @action
  setWorkflowId(workflowId: string) {
    this.workflowId = workflowId;
  }

  @action
  clearWorkflowId() {
    this.workflowId = null;
  }

  @action
  clearPendingInvoices() {
    this.pendingInvoices.replace([]);
  }

  @action
  markAsWaitingForBankConfirmation(paymentIntentId: string, requiresCustomerApproval: boolean, completesAt: number) {
    if (this.status.code === ServerContractStatusCode.Pending) {
      this.bankConfirmationDetails = BankConfirmationDetails.create({
        paymentIntentId,
        requiresCustomerApproval,
        completesAt,
        acknowledged: false,
      });
    }
  }

  @action
  clearBankConfirmationDetails() {
    this.bankConfirmationDetails = null;
  }

  get pendingBankConfirmation() {
    if (this.status.code !== ServerContractStatusCode.Pending || !this.bankConfirmationDetails) {
      return null;
    }

    const { requiresCustomerApproval, completesAt, acknowledged, paymentIntentId } = this.bankConfirmationDetails;

    return {
      paymentIntentId,
      requiresCustomerApproval,
      completesAt: DateTime.fromMillis(completesAt),
      acknowledged,
    };
  }

  get isWaitingForBankConfirmation() {
    return !!this.pendingBankConfirmation;
  }

  get pendingPaymentType(): "plan-change" | "credit-purchase" | null {
    if (!this.workflowId) {
      return null;
    }

    return pendingPaymentTypeForWorkflowId(this.workflowId);
  }

  @action
  acknowledgeBankConfirmationRequired() {
    if (!this.bankConfirmationDetails) {
      return;
    }

    this.bankConfirmationDetails.acknowledge();
  }

  get hasPendingPayments() {
    return this.isWaitingForBankConfirmation || this.pendingInvoices.length > 0;
  }
}

export const NominalBillingContractSnapshot: SnapshotIn<typeof BillingContract> = {
  status: { code: ServerContractStatusCode.Nominal },
};

export const DefaultNominalBillingContract = types.optional(BillingContract, NominalBillingContractSnapshot);
